/**
 * @class
 * @memberOf P3.math
 */
export default class Color {
    /**
     * @param {number|string} [hex=0x0]
     * @param {number} [alpha=0]
     */
    constructor(hex = 0x0, alpha = 1) {
        /**
         * @member {number}
         */
        this._r = 0;

        /**
         * @member {number}
         */
        this._g = 0;

        /**
         * @member {number}
         */
        this._b = 0;

        /**
         * @member {number}
         */
        this._a = alpha;

        /**
         * @member {number}
         */
        this._h = 0;

        /**
         * @member {number}
         */
        this._s = 0;

        /**
         * @member {number}
         */
        this._v = 0;

        this.setHex(hex);
    }

    /**
     * @param {number} value
     * @returns {string}
     */
    static hexNumberToString(value) {
        const pad = "#000000";
        const hex = parseInt(value).toString(16);
        return pad.substring(0, pad.length - hex.length) + hex;
    }

    /**
     * @param {Color} a
     * @param {Color} b
     * @param {number} [t=0.5]
     * @returns {Color}
     */
    static lerp(a, b, t = 0.5) {
        const c = new P3.Color(a.toNumber());
        c.lerp(b, t);
        return c;
    }

    /**
     * Set the color from a hex formatted string.
     * http://stackoverflow.com/questions/5623838/rgb-to-hex-and-hex-to-rgb
     *
     * @param {number|string} value
     * @returns {Color}
     */
    setHex(value) {
        if (typeof value === "number") {
            value = Color.hexNumberToString(value);
        }

        const shorthandRegex = /^#?([a-f\d])([a-f\d])([a-f\d])$/i;
        const hex = value.replace(shorthandRegex, function(m, r, g, b) {
            return r + r + g + g + b + b;
        });

        const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
        if (result) {
            this._r = parseInt(result[1], 16) / 255;
            this._g = parseInt(result[2], 16) / 255;
            this._b = parseInt(result[3], 16) / 255;

            this._updateHSV();
        }
        return this;
    }

    /**
     * Set the color from another color.
     *
     * @param {Color} color
     * @returns {Color}
     */
    setColor(color) {
        this._r = color.r;
        this._g = color.g;
        this._b = color.b;
        this._a = color.a;
        this._h = color.h;
        this._s = color.s;
        this._v = color.v;
        return this;
    }

    /**
     * Set the color from RGB values.
     *
     * @param {number} r - The red value to set.
     * @param {number} g - The green value to set.
     * @param {number} b - The blue value to set.
     * @returns {Color}
     */
    setRGB(r, g, b) {
        this._r = r;
        this._g = g;
        this._b = b;

        this._updateHSV();
        return this;
    }

    /**
     * Set the color from the RGBA values.
     *
     * @param {number} r - The red value to set.
     * @param {number} g - The green value to set.
     * @param {number} b - The blue value to set.
     * @param {number} a - The alpha value to set.
     * @returns {Color}
     */
    setRGBA(r, g, b, a) {
        this._a = a;

        this.setRGB(r, g, b);
        return this;
    }

    /**
     * Set the color from the HSV values.
     *
     * @param {number} h - The hue value to set.
     * @param {number} s - The saturation value to set.
     * @param {number} v - The brightness value to set.
     * @returns {Color}
     */
    setHSV(h, s, v) {
        this._h = h;
        this._s = s;
        this._v = v;

        this._updateRGB();
        return this;
    }

    /**
     * Desaturate the color.
     *
     * @param {number} [r] - Desaturation r ratio.
     * @param {number} [g] - Desaturation g ratio.
     * @param {number} [b] - Desaturation b ratio.
     * @returns {Color}
     */
    desaturate(r = 0.59, g = 0.30, b = 0.11) {
        this._r = this._g * r + this._r * g + this._b * b;
        this._g = this._r;
        this._b = this._r;

        this._updateHSV();
        return this;
    }

    /**
     * Invert the color.
     *
     * @returns {Color}
     */
    invert() {
        this._r = 1 - this._r;
        this._g = 1 - this._g;
        this._b = 1 - this._b;

        this._updateHSV();
        return this;
    }

    /**
     * Blend the current color with the given one.
     *
     * @param {Color} to
     * @param {number} [t=0.5] A number between 0 and 1.
     */
    lerp(to, t = 0.5) {
        t = Math.min(1, Math.max(0, t));

        this._r = this._r * (1 - t) + to.r * t;
        this._g = this._g * (1 - t) + to.g * t;
        this._b = this._b * (1 - t) + to.b * t;

        this._updateHSV();
        return this;
    }

    /**
     * Randomize the color's values
     *
     * @returns {Color}
     */
    random() {
        this._r = Math.random();
        this._g = Math.random();
        this._b = Math.random();

        this._updateHSV();
        return this;
    }

    /**
     * Normalize the color's values.
     *
     * @private
     */
    _normalize() {
        this._r = this._r < 0 ? 0 : (this._r > 1 ? 1 : this._r);
        this._g = this._g < 0 ? 0 : (this._g > 1 ? 1 : this._g);
        this._b = this._b < 0 ? 0 : (this._b > 1 ? 1 : this._b);
        this._a = this._a < 0 ? 0 : (this._a > 1 ? 1 : this._a);
        this._h = Number((this._h - Math.floor(this._h)).toPrecision(8));
        this._s = this._s < 0 ? 0 : (this._s > 1 ? 1 : this._s);
        this._v = this._v < 0 ? 0 : (this._v > 1 ? 1 : this._v);
    }

    /**
     * Update the color's RGB values from the HSV ones.
     * http://stackoverflow.com/questions/17242144/javascript-convert-hsb-hsv-color-to-rgb-accurately
     *
     * @private
     */
    _updateRGB() {
        this._normalize();

        const i = Math.floor(this._h * 6);
        const f = this._h * 6 - i;
        const p = this._v * (1 - this._s);
        const q = this._v * (1 - f * this._s);
        const t = this._v * (1 - (1 - f) * this._s);

        switch (i % 6) {
            case 0:
                this._r = this._v;
                this._g = t;
                this._b = p;
                break;

            case 1:
                this._r = q;
                this._g = this._v;
                this._b = p;
                break;

            case 2:
                this._r = p;
                this._g = this._v;
                this._b = t;
                break;

            case 3:
                this._r = p;
                this._g = q;
                this._b = this._v;
                break;

            case 4:
                this._r = t;
                this._g = p;
                this._b = this._v;
                break;

            case 5:
                this._r = this._v;
                this._g = p;
                this._b = q;
                break;
        }
    }

    /**
     * Update the color's HSV values from the RGB ones.
     * http://www.deadbeef.com/converting_rgb_to_hsv_in_c/
     *
     * @private
     */
    _updateHSV() {
        this._normalize();

        const maxC = Math.max(this._r, this._g, this._b);
        const minC = Math.min(this._r, this._g, this._b);
        const delta = maxC - minC;

        let h = 0;
        let s = 0;
        const v = maxC;

        if (delta == 0) {
            h = 0;
            s = 0;
        } else {
            s = delta / maxC;
            const dR = 60 * (maxC - this._r) / delta + 180;
            const dG = 60 * (maxC - this._g) / delta + 180;
            const dB = 60 * (maxC - this._b) / delta + 180;
            if (this._r == maxC)
                h = dB - dG;
            else if (this._g == maxC)
                h = 120 + dR - dB;
            else
                h = 240 + dG - dR;
        }

        if (h < 0) h += 360;
        if (h >= 360) h -= 360;

        this._h = h / 360;
        this._s = s;
        this._v = v;
    }

    /**
     * Return the hex value of the color.
     * http://stackoverflow.com/questions/5623838/rgb-to-hex-and-hex-to-rgb
     *
     * @returns {string}
     */
    toHex() {
        return "#" + ((1 << 24) + (Math.round(this._r * 255) << 16) + (Math.round(this._g * 255) << 8) + Math.round(this._b * 255)).toString(16).slice(1);
    }

    /**
     * @returns {number}
     */
    toNumber() {
        return (Math.round(this._r * 255) << 16) + (Math.round(this._g * 255) << 8) + Math.round(this._b * 255);
    }

    /**
     * Return the CSS RGBA value.
     *
     * @returns {string}
     */
    toCSS() {
        return "rgba(" + Math.round(this._r * 255) + "," + Math.round(this._g * 255) + "," + Math.round(this._b * 255) + "," + this._a + ")";
    }

    /**
     * Get the color's red value.
     *
     * @returns {number}
     */
    get r() {
        return this._r;
    }

    /**
     * Set the color's red value.
     *
     * @param {number} value - The red value to set.
     */
    set r(value) {
        this._r = value;
        this._updateHSV();
    }

    /**
     * Get the color's green value.
     *
     * @returns {number}
     */
    get g() {
        return this._g;
    }

    /**
     * Set the color's green value.
     *
     * @param {number} value - The green value to set.
     */
    set g(value) {
        this._g = value;
        this._updateHSV();
    }

    /**
     * Get the color's blue value.
     *
     * @returns {number}
     */
    get b() {
        return this._b;
    }

    /**
     * Set the color's blue value.
     *
     * @param {number} value - The blue value to set.
     */
    set b(value) {
        this._b = value;
        this._updateHSV();
    }

    /**
     * Get the color's alpha value.
     *
     * @returns {number}
     */
    get a() {
        return this._a;
    }

    /**
     * Set the color's alpha value.
     *
     * @param {number} value - The alpha value to set.
     */
    set a(value) {
        this._a = value;
    }

    /**
     * Get the color's hue value.
     *
     * @returns {number}
     */
    get h() {
        return this._h;
    }

    /**
     * Set the color's hue value.
     *
     * @param {number} value - The hue value to set.
     */
    set h(value) {
        this._h = value;
        this._updateRGB();
    }

    /**
     * Get the color's saturation value.
     *
     * @returns {number}
     */
    get s() {
        return this._s;
    }

    /**
     * Set the color's saturation value.
     *
     * @param {number} value - The saturation to set.
     */
    set s(value) {
        this._s = value;
        this._updateRGB();
    }

    /**
     * Get the color's brightness value.
     *
     * @returns {number}
     */
    get v() {
        return this._v;
    }

    /**
     * Set the color's brightness value.
     *
     * @param {number} value - The brightness to set.
     */
    set v(value) {
        this._v = value;
        this._updateRGB();
    }

    /**
     * The luminosity of the color.
     * http://stackoverflow.com/questions/596216/formula-to-determine-brightness-of-rgb-color
     *
     * @returns {number}
     */
    get luminosity() {
        return (0.299 * this._r + 0.587 * this._g + 0.114 * this._b)
    }
}
